import { Component, ElementRef, Input, OnChanges } from '@angular/core';
import { orderBy, sumBy, reverse } from 'lodash-es';

// services
import { TranslateService } from '@ngx-translate/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { ColorService } from '@app/shared/services/color.service';
import { HighchartsService } from '@app/shared/services/highcharts.service';
import { SignalCount } from '@app/profile/profile-api.model';
import { Chart } from 'highcharts';

interface BarChartSeriesData {
  name: string;
  data: number[];
  color: string;
}
@Component({
  selector: 'dgx-skill-signal-activity-chart',
  templateUrl: './skill-signal-activity-chart.component.html',
  styleUrls: ['./skill-signal-activity-chart.component.scss'],
})
export class SkillSignalActivityChart
  extends SubscriberBaseDirective
  implements OnChanges
{
  @Input() public signalsCount: SignalCount[];
  @Input() public signalType: string;
  @Input() public isMobile: boolean;

  public isEmpty: boolean = false;
  public maxSignals: number = 6;
  public ratings: SignalCount[];

  private chart: Chart;
  private colorList = ['orange', 'pink', 'ocean', 'forest', 'purple', 'sky'];
  public chartColors = this.colorList.map((c) => this.colors.getColor(c));

  public i18n = this.translateService.instant([
    'Core_AccomplishmentsDisplayName',
    'Core_Articles',
    'Core_AssessmentsDisplayName',
    'Core_AwardsDisplayName',
    'Core_BadgesDisplayName',
    'Core_Books',
    'Core_CertificatesDisplayName',
    'Core_Courses',
    'Core_DegreesDisplayName',
    'Core_Events',
    'Core_ExperiencesDisplayName',
    'Core_ManagerRating',
    'Core_Media',
    'Core_Podcasts',
    'Core_PostsDisplayName',
    'Core_Ratings',
    'Core_SelfRating',
    'Core_Tasks',
    'Core_Videos',
    'Core_Other',
    'dgTagRating_PeerRating',
    'dgUserOverview_SkillCertification',
    'dgUserOverview_SkillReview',
  ]);

  public signalTypes = {
    Accomplishment: 'Core_AccomplishmentsDisplayName',
    Article: 'Core_Articles',
    Assessment: 'Core_AssessmentsDisplayName',
    Award: 'Core_AwardsDisplayName',
    Badge: 'Core_BadgesDisplayName',
    Book: 'Core_Books',
    Certificate: 'Core_CertificatesDisplayName',
    Credential: 'dgUserOverview_SkillCertification',
    Course: 'Core_Courses',
    Degree: 'Core_DegreesDisplayName',
    Evaluation: 'dgUserOverview_SkillReview',
    Event: 'Core_Events',
    Experience: 'Core_ExperiencesDisplayName',
    Manager: 'Core_ManagerRating',
    Media: 'Core_Media',
    Peer: 'dgTagRating_PeerRating',
    Podcast: 'Core_Podcasts',
    Position: 'Core_ExperiencesDisplayName',
    Post: 'Core_PostsDisplayName',
    Rating: 'Core_Ratings',
    Self: 'Core_SelfRating',
    Task: 'Core_Tasks',
    Video: 'Core_Videos',
  };

  public emptyStateSignalSources = {
    OtherTypes: 'Core_Other',
    Podcasts: 'Core_Podcasts',
    Experiences: 'Core_ExperiencesDisplayName',
    Courses: 'Core_Courses',
    Videos: 'Core_Videos',
    Books: 'Core_Books',
    Articles: 'Core_Articles',
  };

  public emptyStateRatings = {
    OtherTypes: 'Core_Other',
    Evaluation: 'dgUserOverview_SkillReview',
    Manager: 'Core_ManagerRating',
    Self: 'Core_SelfRating',
  };

  public emptyStateExperiences = {
    Position: 'Core_ExperiencesDisplayName',
  };

  public emptyStateAchievements = {
    Degree: 'Core_DegreesDisplayName',
    Certificate: 'Core_CertificatesDisplayName',
    Badge: 'Core_BadgesDisplayName',
    Award: 'Core_AwardsDisplayName',
    Accomplishment: 'Core_AccomplishmentsDisplayName',
  };

  public emptyStateLearnings = {
    OtherTypes: 'Core_Other',
    Podcast: 'Core_Podcasts',
    Event: 'Core_Events',
    Course: 'Core_Courses',
    Book: 'Core_Books',
    Assessment: 'Core_AssessmentsDisplayName',
    Article: 'Core_Articles',
  };

  constructor(
    private translateService: TranslateService,
    private element: ElementRef,
    private colors: ColorService,
    private Highcharts: HighchartsService
  ) {
    super();
  }

  public ngAfterViewInit(): void {
    this.setupChart();
  }

  public ngOnChanges(): void {
    this.setupChart();
  }

  private setupChart(): void {
    if (
      !this.signalsCount ||
      sumBy(this.signalsCount, (type: SignalCount) => type.count) === 0
    ) {
      this.buildEmptyState(this.signalType);
    } else {
      this.isEmpty = false;
      this.sortSignals(this.signalsCount);
    }
  }

  /**
   * Builds empty state based on what tab is active
   *
   * @param signalType - active tab string
   */
  public buildEmptyState(signalType: string): void {
    this.isEmpty = true;
    let emptyState;
    switch (signalType) {
      case 'All':
        emptyState = this.emptyStateSignalSources;
        break;
      case 'Ratings':
        emptyState = this.emptyStateRatings;
        break;
      case 'Experiences':
        emptyState = this.emptyStateExperiences;
        break;
      case 'Achievements':
        emptyState = this.emptyStateAchievements;
        break;
      case 'Learnings':
        emptyState = this.emptyStateLearnings;
        break;
    }
    const series = [];
    for (const key in emptyState) {
      const i18nTranslation = this.i18n[emptyState[key]];

      series.push({
        name: `${i18nTranslation} ・ 0`,
        data: [0],
        color: this.colors.getColor('ebony-a18'),
      });
    }

    this.generateChart(series, 0);
  }

  /**
   * Sorts signalsCount array based on active tab (signalType) and converts
   * data to type that the highcharts chart can process
   *
   * @param signalsCount - the list of signals.
   */
  public sortSignals(signalsCount: SignalCount[]): void {
    let signals;
    switch (this.signalType) {
      case 'All':
        signals = this.condenseRatings(signalsCount);
        break;
      case 'Ratings':
        signals = this.sortRatings(signalsCount);
        break;
      case 'Experiences':
        signals = this.filterSignalsByType(signalsCount, 'Experience');
        break;
      case 'Achievements':
        signals = this.filterSignalsByType(signalsCount, 'Achievement');
        break;
      case 'Learnings':
        signals = this.filterSignalsByType(signalsCount, 'Learning');
        break;
    }
    const sum = sumBy(signals, (signal: SignalCount) => signal.count);
    const orderedSignals = this.orderSignals(signals);
    const topSignals: BarChartSeriesData[] = reverse(
      orderedSignals.slice(0, this.maxSignals).reduce((chartData, s, i) => {
        const i18nTranslation = this.i18n[this.signalTypes[s.type]] || s.type;
        const color =
          s.count === 0
            ? this.colors.getColor('ebony-a18')
            : this.chartColors[i];
        chartData.push({
          name: `${i18nTranslation} ・ ${s.count}`,
          data: [s.count],
          color: color,
        });

        return chartData;
      }, [])
    );
    if (['All', 'Ratings', 'Learnings'].includes(this.signalType)) {
      const otherTypes = this.getOtherTypes(orderedSignals);
      topSignals.unshift(otherTypes);
    }
    const series = topSignals;

    if (sum === 0) {
      this.buildEmptyState(this.signalType);
    } else {
      this.generateChart(series, sum);
    }
  }

  public orderSignals(signals: SignalCount[]): SignalCount[] {
    return orderBy(signals, ['count', 'type'], ['desc', 'asc']);
  }

  public filterSignalsByType(
    signals: SignalCount[],
    type: string
  ): SignalCount[] {
    return signals.filter((signal) => signal.skillType === type);
  }

  /**
   * Returns array of internal ratings by default with external ratings which count is greater than 1
   *
   * @param signals - the list of signals.
   */
  public sortRatings(signals: SignalCount[]): SignalCount[] {
    const internalRatings = ['Self', 'Manager', 'Evaluation'];
    const ratings = signals.filter(
      (signal) =>
        signal.skillType === 'Rating' &&
        signal.type.length > 0 &&
        (signal.count > 0 || internalRatings.includes(signal.type))
    );

    return ratings;
  }

  /**
   * Used only when active tab (signalType) is "All", this function filters out all Rating
   * type signals and condenses them to 1 object, then returns object in array with other signal types.
   *
   * @param signalsCount - the list of signals.
   */
  public condenseRatings(signalsCount: SignalCount[]): SignalCount[] {
    const signals = [];
    const ratings = this.filterSignalsByType(signalsCount, 'Rating');
    const otherSignals = signalsCount.filter(
      (signal) => signal.skillType !== 'Rating'
    );
    const countSum = sumBy(ratings, (rating: SignalCount) => rating.count);

    return [
      {
        type: 'Rating',
        skillType: 'Rating',
        count: countSum,
      },
      ...otherSignals,
    ];
  }

  /**
   * This function takes ordered list of signals, slices out top 6, condenses remaining signals into 1 "Other Types"
   * object, and returns converted data to type that the highcharts chart can process
   *
   * @param signals - the list of signals.
   */
  public getOtherTypes(signals: SignalCount[]): BarChartSeriesData {
    const otherTypes = signals.slice(this.maxSignals);
    const countSum = sumBy(otherTypes, (type: SignalCount) => type.count);
    return {
      name: `${this.i18n.Core_Other} ・ ${countSum}`, // TODO: pass through different i18n strings
      data: [countSum],
      color: this.colors.getColor('ebony-a18'),
    };
  }

  private generateChart(series: BarChartSeriesData[], sum: number): void {
    const container =
      this.element.nativeElement.querySelector('.signal-bar-chart');

    this.Highcharts.createChart(container, {
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'inherit',
        },
        height: this.isMobile ? 200 : 100,
      },
      exporting: {
        enabled: false,
      },
      title: {
        text: null,
      },
      legend: {
        reversed: true,
        align: 'left',
        x: -10,
        symbolHeight: 10,
        itemStyle: {
          fontFamily: 'inherit',
          color: this.colors.getColor('ebony-a61'),
          fontSize: '1rem',
          lineHeight: '1.5rem',
          fontWeight: 400,
        },
        navigation: {
          enabled: false,
        },
      },
      xAxis: {
        labels: {
          enabled: false,
        },
        lineWidth: 0,
        tickWidth: 0,
        min: 0,
        max: 0,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
        lineWidth: 0,
        title: {
          text: '',
        },
        min: 0,
        ceiling: sum,
      },
      tooltip: {
        useHTML: true,
        backgroundColor: this.colors.getColor('ebony'),
        style: {
          color: this.colors.getColor('white'),
        },
        borderWidth: 0,
        borderRadius: 3,
        padding: 0,
        shadow: false,
        outside: true,
        animation: false,
        formatter: function () {
          return `
              <div style="padding: 0.75rem 1rem">
                ${this.series.name}
              </div>
          `;
        },
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          stacking: 'normal',
          pointWidth: 8,
          borderRadius: 3,
        },
      },
      series: series,
    }).subscribe((chart) => (this.chart = chart));
  }
}
